import React, { ReactNode } from 'react'
import { ButtonIconPosition, ButtonVariants, TextPosition } from '../Button'
import { getDefaultClasses, getIconClasses } from '../Button/helpers/buttonClassHelpers'

export interface LinkAsButtonProps extends React.HTMLAttributes<HTMLAnchorElement> {
    href?: string
    variant?: ButtonVariants
    children?: ReactNode
    icon?: ReactNode
    textPosition?: TextPosition
    iconPosition?: ButtonIconPosition
    labelIsForScreenReadersOnly?: boolean
    screenReaderLabel?: ReactNode
    form?: string
    /** Allows the button to take full height of parent container. Results in no vertical padding and height of 100% */
    contained?: boolean
    onClick?: (e?: React.MouseEvent<HTMLAnchorElement>) => void
    download?: string | ReactNode
    disabled?: boolean
}

export const LinkAsButton = React.forwardRef<HTMLAnchorElement, LinkAsButtonProps>(
    (
        {
            variant = ButtonVariants.SECONDARY,
            children,
            icon,
            textPosition = TextPosition.CENTER,
            iconPosition = ButtonIconPosition.LEFT,
            labelIsForScreenReadersOnly = false,
            screenReaderLabel,
            href,
            contained = false,
            download,
            disabled,
            ...props
        },
        ref,
    ) => {
        const classes = getDefaultClasses({ textPosition, variant, contained, className: props.className, disabled })
        const iconClasses = getIconClasses({ iconPosition, hasChildren: !!children, labelIsForScreenReadersOnly })

        return (
            <a
                href={href}
                {...(props as React.HTMLAttributes<HTMLAnchorElement>)}
                className={classes}
                download={download}
                ref={ref as React.Ref<HTMLAnchorElement>}
            >
                <span className="inline-flex items-center">
                    {icon && <span className={iconClasses}>{icon}</span>}

                    {screenReaderLabel && <span className="sr-only">{screenReaderLabel}</span>}
                    {children && children}
                </span>
            </a>
        )
    },
)
